import React, { useState, useEffect, useRef } from 'react';
import './XmlImport.css';
import { uploadXmlImport, getImportJobStatus, getImportJobs } from '../services/apiService';
import Button from './ui/Button';
import Tabs from './ui/Tabs';

const TABS = [
    { label: 'Aktuell' },
    { label: 'Früher' },
];

const XmlImport = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [importJob, setImportJob] = useState(null);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [previousJobs, setPreviousJobs] = useState([]);
    const [previousJobsLoading, setPreviousJobsLoading] = useState(false);
    const fileInputRef = useRef(null);
    const pollingIntervalRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && (selectedFile.type === 'text/xml' || selectedFile?.name.endsWith('.xml'))) {
            setFile(selectedFile);
            setError(null);
        } else if (selectedFile) {
            setError('Bitte wählen Sie eine gültige XML-Datei aus.');
            setFile(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && (droppedFile.type === 'text/xml' || droppedFile.name.endsWith('.xml'))) {
            setFile(droppedFile);
            setError(null);
        } else if (droppedFile) {
            setError('Bitte ziehen Sie eine gültige XML-Datei in den Bereich.');
            setFile(null);
        }
    };

    const triggerFileSelect = () => {
        fileInputRef.current.click();
    };

    const handleUpload = async () => {
        if (!file) return;

        try {
            setUploading(true);
            setError(null);
            setImportJob(null);
            
            const response = await uploadXmlImport(file);
            if (response.success) {
                setImportJob(response.data);
                startPolling(response.data.jobId);
            }
        } catch (err) {
            console.error('Fehler beim Upload:', err);
            setError(err.message || 'Fehler beim Hochladen der Datei.');
            setUploading(false);
        }
    };

    const startPolling = (jobId) => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = setInterval(async () => {
            try {
                const response = await getImportJobStatus(jobId);
                if (response.success) {
                    const statusData = response.data;
                    setImportJob(statusData);

                    if (statusData.status === 'COMPLETED' || statusData.status === 'FAILED') {
                        stopPolling();
                        setUploading(false);
                    }
                }
            } catch (err) {
                console.error('Fehler beim Abrufen des Job-Status:', err);
                stopPolling();
                setUploading(false);
                setError('Fehler beim Abrufen des Verarbeitungsstatus.');
            }
        }, 1000);
    };

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    useEffect(() => {
        return () => stopPolling();
    }, []);

    const toggleRowExpansion = (index) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    // Frühere Importe laden, wenn der Tab gewechselt wird
    const loadPreviousJobs = async () => {
        try {
            setPreviousJobsLoading(true);
            const response = await getImportJobs();
            if (response.success && response.data) {
                setPreviousJobs(response.data);
            }
        } catch (err) {
            console.error('Fehler beim Laden früherer Importe:', err);
        } finally {
            setPreviousJobsLoading(false);
        }
    };

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);
        if (tabIndex === 1) {
            loadPreviousJobs();
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '–';
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
        const timePart = date.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        return <>{datePart}<br />{timePart}</>;
    };

    const failedEntries = importJob?.protokoll?.filter(entry => entry.ergebnis === 'FEHLER') || [];

    return (
        <div className="xml-import-container">
            <div className="xml-import-card">
                <h2 className="xml-import-title">Daten-Import</h2>
                
                <Tabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                <div className="tab-content">
                    {activeTab === 0 && (
                        <div className="xml-import-tab-panel">
                            <div 
                                className={`drag-drop-area ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={triggerFileSelect}
                            >
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleFileChange} 
                                    accept=".xml" 
                                    style={{ display: 'none' }}
                                />
                                <div className="drag-drop-content">
                                    <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                                    </svg>
                                    {file ? (
                                        <div className="selected-file-info">
                                            <span className="file-name">{file.name}</span>
                                            <span className="file-size">({(file.size / 1024).toFixed(2)} KB)</span>
                                        </div>
                                    ) : (
                                        <p>XML-Datei hierher ziehen oder klicken zum Auswählen</p>
                                    )}
                                </div>
                            </div>

                            <div className="upload-actions">
                                <Button 
                                    onClick={handleUpload} 
                                    disabled={!file || uploading}
                                    className="upload-button"
                                >
                                    {uploading && importJob?.status !== 'COMPLETED' && importJob?.status !== 'FAILED' 
                                        ? 'Verarbeite...' 
                                        : 'Upload'}
                                </Button>
                            </div>

                            {error && <div className="import-error-message">{error}</div>}

                            {importJob && (
                                <div className="import-status-area">
                                    <div className="status-summary">
                                        <div className="status-item">
                                            <span className="status-label">Status:</span>
                                            <span className={`status-value status-${importJob.status.toLowerCase()}`}>
                                                {importJob.status}
                                            </span>
                                        </div>
                                        <div className="status-stats">
                                            <div className="stat-box">
                                                <span className="stat-value">{importJob.abteilungenImportiert || 0}</span>
                                                <span className="stat-label">Erfolgreich</span>
                                            </div>
                                            <div className="stat-box">
                                                <span className="stat-value">{importJob.abteilungenFehlgeschlagen || 0}</span>
                                                <span className="stat-label">Fehlgeschlagen</span>
                                            </div>
                                            <div className="stat-box">
                                                <span className="stat-value">{importJob.abteilungenUebersprungen || 0}</span>
                                                <span className="stat-label">Übersprungen</span>
                                            </div>
                                            <div className="stat-box">
                                                <span className="stat-value">{importJob.abteilungenGesamt || 0}</span>
                                                <span className="stat-label">Gesamt</span>
                                            </div>
                                        </div>
                                    </div>

                                    {importJob.fehlerMeldung && (
                                        <div className="global-error">
                                            <strong>Fehler:</strong> {importJob.fehlerMeldung}
                                        </div>
                                    )}

                                    {failedEntries.length > 0 && (
                                        <div className="failed-entries-area">
                                            <h4 className="failed-title">Fehlgeschlagene Einträge</h4>
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th>Abteilungsnummer</th>
                                                        <th>Ursache des Fehlschlags</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {failedEntries.map((entry, index) => {
                                                        const isExpanded = expandedRows.has(index);
                                                        return (
                                                            <tr key={index} className="failed-row">
                                                                <td className="department-cell">{entry.abteilungNummer || `Position ${entry.position}`}</td>
                                                                <td className="error-detail-cell">
                                                                    <div className={`error-text ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                                                        {entry.fehlerDetail}
                                                                    </div>
                                                                    {entry.fehlerDetail && entry.fehlerDetail.split('\n').length > 3 && (
                                                                        <button 
                                                                            className="toggle-expand-button"
                                                                            onClick={() => toggleRowExpansion(index)}
                                                                        >
                                                                            {isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 1 && (
                        <div className="xml-import-tab-panel common-section">
                            {previousJobsLoading ? (
                                <p className="loading-text">Lade frühere Importe…</p>
                            ) : previousJobs.length === 0 ? (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-col">Import</th>
                                            <th>Erfolgreich</th>
                                            <th>Fehlgeschlagen</th>
                                            <th>Übersprungen</th>
                                            <th>Gesamt</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td colSpan="5" className="no-data">Keine früheren Importe vorhanden.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-col">Import</th>
                                            <th>Erfolgreich</th>
                                            <th>Fehlgeschlagen</th>
                                            <th>Übersprungen</th>
                                            <th>Gesamt</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previousJobs.map((job) => (
                                            <tr key={job.jobId}>
                                                <td className="sticky-col">{formatDateTime(job.abgeschlossenAm)}</td>
                                                <td>{job.abteilungenImportiert || 0}</td>
                                                <td>{job.abteilungenFehlgeschlagen || 0}</td>
                                                <td>{job.abteilungenUebersprungen || 0}</td>
                                                <td>{job.abteilungenGesamt || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default XmlImport;
